import {observer} from 'mobx-react';
import {lazy, useState} from 'react';
import {useStoreContext} from '../../store/store-context';
import {beamMasteryTasks} from '../../utils/firebase-request';
import {CreateGoalController} from './create-goal-controller';
import {CreateGoalRequestImpl} from './create-goal-request';
import {usePostHogHook} from '@hooks/post-hog';
import {useTags} from '@hooks/tags';
import {updateFirebaseData} from '@utils/firebase-handler';
import {useQueryClient} from 'react-query';
import {invalidateGoalQueries} from '@utils/goal-query-cache';
import {posthogLogs} from '@utils/posthog-logger';

const CreateGoalPage = lazy(() =>
  import('./create-goal-page').then((module) => ({
    default: module.CreateGoalPage,
  })),
);

const CreateGoalForm = lazy(() =>
  import('./create-goal-form').then((module) => ({
    default: module.CreateGoalForm,
  })),
);

export const installCreateGoalPage = () => {
  const request = new CreateGoalRequestImpl();
  const controller = new CreateGoalController(request);

  const FormComponent = observer(() => {
    const {
      goalsState: {setShowGoalForm},
    } = useStoreContext();

    const [loading, setLoading] = useState(false);

    const queryClient = useQueryClient();

    const {tags} = useTags(`goal_tags`);
    const formattedTags = tags.filter((tag) => !!tag.tag?.name);

    const {postHogCapture, postHog} = usePostHogHook();

    const onSubmit = async (data: any) => {
      setLoading(true);

      const data_ = data;

      const response = await controller.createGoal(data_);
      setLoading(false);

      if (data?.isKpi) {
        response && beamMasteryTasks('create_kpi', true);
      } else if (response) {
        beamMasteryTasks('create_objective', true);
      }

      if (response) {
        postHogCapture('goal_created', undefined, {
          is_kpi: Boolean(data?.isKpi),
        });
        posthogLogs.goalCreated(postHog, {
          is_kpi: Boolean(data?.isKpi),
        });
      }

      if (response && data.meta?.tags) {
        formattedTags.forEach((tag) => {
          if (data?.meta.tags.includes(tag?.tag?.name)) {
            updateFirebaseData(`goal_tags/${tag.key}`, {
              name: tag?.tag.name,
              goals: tag?.tag?.goals
                ? [...tag?.tag?.goals, response?.id]
                : [response?.id],
            });
          }
        });
      }

      response && setShowGoalForm(false);

      response && (await invalidateGoalQueries(queryClient));
    };

    return <CreateGoalForm onSubmit={onSubmit} loading={loading} />;
  });

  const Page = observer(({exploreTemplates}: {exploreTemplates?: boolean}) => (
    <CreateGoalPage
      FormComponent={FormComponent}
      exploreTemplates={exploreTemplates}
    />
  ));

  return {
    Page,
  };
};
